import React, { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarItem from './SidebarItem';
import SidebarUserInfo from './SidebarUserInfo';
import SidebarConnectedUsers from './SidebarConnectedUsers';
import { usePermissions } from '../../hooks/usePermissions';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import './Sidebar.css';

/**
 * Configuration des items de menu avec leurs routes
 */
const MENU_ITEMS = {
  main: [
    { id: 'dashboard', name: 'Tableau de bord', icon: '📊', path: '/dashboard' },
    { id: 'planning', name: 'Planning', icon: '📅', path: '/planning' },
    { id: 'my-planning', name: 'Mon planning', icon: '👤', path: '/my-planning' },
    { id: 'clients', name: 'Clients', icon: '🏰', path: '/clients' },
    { id: 'users', name: 'Utilisateurs', icon: '👥', path: '/users' },
    { id: 'hr', name: 'RH', icon: '📋', path: '/hr' },
    { id: 'worksheets', name: 'Feuilles', icon: '📝', path: '/worksheets' },
    { id: 'work-sheets', name: 'Fiches chantier', icon: '📄', path: '/work-sheets' },
    { id: 'my-leaves', name: 'Mes congés', icon: '🏖️', path: '/my-leaves' },
    { id: 'leaves-validation', name: 'Valid. congés', icon: '✅', path: '/leaves-validation' },
    { id: 'my-expenses', name: 'Mes frais', icon: '💰', path: '/my-expenses' },
    { id: 'expenses-validation', name: 'Gestion frais', icon: '💼', path: '/expenses-validation' }
  ],
  hidden: [
    { id: 'locations', name: 'Lieux', icon: '🍇', path: '/locations' },
    { id: 'groups', name: 'Groupes', icon: '🚛', path: '/groups' }
  ],
  admin: [
    { id: 'feedback', name: 'Retours Tests', icon: '✅', path: '/feedback' },
    { id: 'holidays', name: 'Jours fériés', icon: '🗓️', path: '/holidays' },
    { id: 'permissions', name: 'Permissions', icon: '🔐', path: '/permissions' },
    { id: 'email-templates', name: 'Templates Email', icon: '📧', path: '/email-templates' },
    { id: 'user-import', name: 'Import Personnel', icon: '📥', path: '/user-import' },
    { id: 'analytics', name: 'Analytics', icon: '📈', path: '/analytics' },
    { id: 'ocr-test', name: 'Test OCR', icon: '🔍', path: '/ocr-test' },
    { id: 'distance-api', name: 'APIs Distance', icon: '🗺️', path: '/distance-api' }
  ]
};

// Exporter les items pour MainLayout (utile pour les routes)
export const ALL_MENU_ITEMS = [...MENU_ITEMS.main, ...MENU_ITEMS.hidden, ...MENU_ITEMS.admin];

/**
 * Icône chevron pour le bouton toggle
 */
const ChevronIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

/**
 * Composant Sidebar principal
 */
const Sidebar = ({
  user,
  isCollapsed,
  isMobileOpen,
  onToggle,
  onLogout,
  onCloseMobile
}) => {
  const location = useLocation();
  const { canAccessModule, isAdmin } = usePermissions(user);
  const [pendingInterimCount, setPendingInterimCount] = useState(0);

  // Charger le nombre d'intérimaires en attente de qualification
  useEffect(() => {
    const fetchPendingCount = async () => {
      // Seulement pour les utilisateurs qui peuvent gérer les utilisateurs
      if (!canAccessModule('users')) return;

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(API_ENDPOINTS.USERS_PENDING_REVIEW, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setPendingInterimCount(data.count || 0);
        }
      } catch (err) {
        console.error('Erreur chargement pending review:', err);
      }
    };

    fetchPendingCount();
    // Rafraîchir toutes les 2 minutes
    const interval = setInterval(fetchPendingCount, 120000);
    return () => clearInterval(interval);
  }, [canAccessModule]);

  // Filtrer les items selon les permissions et personnaliser les noms
  const accessibleMainItems = useMemo(() => {
    return MENU_ITEMS.main
      .filter(item => canAccessModule(item.id));
  }, [canAccessModule]);

  const accessibleAdminItems = useMemo(() => {
    return MENU_ITEMS.admin.filter(item => canAccessModule(item.id));
  }, [canAccessModule]);

  // Gérer le clic sur un item (ferme le menu mobile)
  const handleItemClick = () => {
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  // Classes CSS dynamiques
  const sidebarClasses = [
    'sidebar',
    isMobileOpen ? 'mobile-open' : ''
  ].filter(Boolean).join(' ');

  return (
    <aside
      className={sidebarClasses}
      data-collapsed={isCollapsed}
      role="navigation"
      aria-label="Menu principal"
    >
      {/* Header avec logo et toggle */}
      <div className="sidebar-header">
        <img
          src="/logo_connect_transparant.png"
          alt="R-Connect"
          className="sidebar-logo"
        />
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          aria-label={isCollapsed ? 'Ouvrir le menu' : 'Réduire le menu'}
          title={isCollapsed ? 'Ouvrir le menu' : 'Réduire le menu'}
        >
          <ChevronIcon />
        </button>
      </div>

      {/* Navigation principale */}
      <nav className="sidebar-nav">
        {accessibleMainItems.map(item => (
          <SidebarItem
            key={item.id}
            item={item}
            isCollapsed={isCollapsed}
            onClick={handleItemClick}
            badge={item.id === 'users' ? pendingInterimCount : 0}
          />
        ))}

        {/* Section Admin */}
        {accessibleAdminItems.length > 0 && (
          <>
            <div className="sidebar-divider" />
            <div className="sidebar-section-title">Administration</div>
            {accessibleAdminItems.map(item => (
              <SidebarItem
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                onClick={handleItemClick}
              />
            ))}
          </>
        )}
      </nav>

      {/* Utilisateurs connectés */}
      <SidebarConnectedUsers
        isCollapsed={isCollapsed}
        userRole={user?.role}
      />

      {/* Infos utilisateur en bas */}
      <SidebarUserInfo
        user={user}
        isCollapsed={isCollapsed}
        onLogout={onLogout}
      />
    </aside>
  );
};

export default React.memo(Sidebar);
